import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Table,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Pagination,
  Loader,
  Center,
  Select,
} from "@mantine/core";
import { IconPlus, IconSearch, IconArrowLeft, IconCalendar } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAdminSeasonListQuery, SeasonStatus } from "generated/graphql";

const PAGE_SIZE = 10;

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "gray",
  SCHEDULED: "blue",
  ACTIVE: "green",
  CONCLUDED: "orange",
  ARCHIVED: "red",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "ACTIVE", label: "Active" },
  { value: "CONCLUDED", label: "Concluded" },
  { value: "ARCHIVED", label: "Archived" },
];

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * SeasonManagement (Admin)
 *
 * Lists all seasons with search, status filter, pagination, and navigation to edit/create.
 * Uses real API data from the AdminSeasonList GraphQL query.
 */
export default function SeasonManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [activePage, setActivePage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data, loading, error, refetch } = useAdminSeasonListQuery({
    variables: {
      skip: (activePage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
      status: statusFilter ? (statusFilter as SeasonStatus) : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  // Map API data to table format
  const seasons = useMemo(() => {
    return (data?.seasons?.items ?? []).map((s) => ({
      id: s.id,
      seasonNumber: s.seasonNumber,
      displayName: s.displayName,
      tag: s.tag,
      status: s.status,
      startsAt: s.startsAt,
      endsAt: s.endsAt,
    }));
  }, [data]);

  const totalCount = data?.seasons?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  // Client-side filtering of current page results
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return seasons;
    return seasons.filter((s) =>
      [s.displayName, s.tag, String(s.seasonNumber)].some((v) =>
        v.toLowerCase().includes(q)
      )
    );
  }, [query, seasons]);

  function onSelectSeason(id: string) {
    navigate(`/admin-hub/seasons/${id}`);
  }

  function onCreateSeason() {
    navigate(`/admin-hub/seasons/create`);
  }

  function handleStatusChange(value: string | null) {
    setStatusFilter(value || "");
    setActivePage(1);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub")}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>
      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">
          Season Management
        </Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          Manage competitive seasons. Seasons progress through Draft → Scheduled → Active → Concluded → Archived.
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <TextInput
              placeholder="Search by name or tag"
              leftSection={<IconSearch size={16} />}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              className="w-full sm:max-w-xs"
            />
            <Select
              placeholder="Filter by status"
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
              clearable
              className="w-full sm:w-40"
            />
          </div>
          <Button
            leftSection={<IconPlus />}
            className="bg-blue-500 hover:bg-blue-600"
            onClick={onCreateSeason}
            size="sm"
          >
            Create Season
          </Button>
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Tag</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Start Date</Table.Th>
                <Table.Th>End Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="red">
                      Failed to load seasons.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      {query || statusFilter
                        ? "No seasons match your filters."
                        : "No seasons found. Create one to get started."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                filtered.map((s) => (
                  <Table.Tr
                    key={s.id}
                    onClick={() => onSelectSeason(s.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open season ${s.displayName}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onSelectSeason(s.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Table.Td>{s.seasonNumber}</Table.Td>
                    <Table.Td fw={500}>{s.displayName}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {s.tag}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={STATUS_COLORS[s.status] || "gray"}
                        variant="light"
                      >
                        {s.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <IconCalendar size={14} />
                        {formatDate(s.startsAt)}
                      </Group>
                    </Table.Td>
                    <Table.Td>{formatDate(s.endsAt)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Text
            size="sm"
            c={isDark ? "gray.5" : "gray.6"}
            className="text-center sm:text-left"
          >
            Showing {filtered.length} of {totalCount} season
            {totalCount === 1 ? "" : "s"}
          </Text>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={activePage}
              onChange={(page) => {
                setActivePage(page);
                refetch({
                  skip: (page - 1) * PAGE_SIZE,
                  take: PAGE_SIZE,
                  status: statusFilter ? (statusFilter as SeasonStatus) : undefined,
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="sm"
              className="justify-center sm:justify-end"
            />
          )}
        </div>
      </div>
    </Container>
  );
}
